'use client';

import { Link } from 'react-router-dom';
import { JsonLd } from '@/seo/JsonLd';
import type { Product } from '@/data/products';
import { products } from '@/data/products';
import { ProductCard } from '@/components/ProductCard/ProductCard';
import { Accordion } from '@/components/Accordion/Accordion';
import { Tabs } from '@/components/Tabs/Tabs';
import { IconWhatsApp, IconPhone, IconStar } from '@/components/Icons/Icons';
import { buildWhatsAppUrl, SALES_PHONE_READABLE } from '@/utils/whatsapp';
import { SITE_DOMAIN, toAbsoluteUrl } from '@/utils/url';
import styles from './ProductDetail.module.css';

const sakaryaProduct: Product = {
  slug: 'sakarya-bondex-sunger',
  name: 'Sakarya Bondex Sünger',
  category: 'Bondex Sünger',
  shortDescription:
    'Sakarya bondex sünger; konut, ofis, üretim ve teknik hacim projelerinde ses ve titreşim kontrolü için yüksek yoğunluklu çözüm sunar.',
  description:
    'Sakarya bondex sünger; rebonded süngerpan yapısıyla darbe ve titreşim kaynaklı sesleri azaltmaya yardımcı olan, Sakarya içi projelere özel ses yalıtım çözümüdür.',
  imagePath: '/images/bondex-6.webp',
  sku: 'BDX-SKY',
  mpn: 'BDX-SKY',
  priceTRY: 130,
  priceUnit: 'm2',
  faqs: [
    {
      question: 'Sakarya bondex sünger nerelerde kullanılır?',
      answer:
        'Konut, villa, ofis, atölye, depo, teknik oda ve makine hacimlerinde kullanılır. Sakarya’da hem konut hem sanayi projelerinde tercih edilir.',
    },
    {
      question: 'Sakarya içinde teslimat süresi ne kadar?',
      answer:
        'Stok durumuna göre Sakarya merkez ve ilçelerde teslimatlar çoğunlukla 1-3 iş günü içinde planlanır. Büyük projelerde etaplı sevkiyat yapılabilir.',
    },
    {
      question: 'Hangi kalınlık önerilir?',
      answer:
        'Hafif ihtiyaçlarda 10-20mm, orta-yüksek şikayetlerde 30-40mm, zorlayıcı teknik senaryolarda 50mm ve bariyerli kombinasyonlar önerilir.',
    },
    {
      question: "Sakarya'da keşif desteği var mı?",
      answer:
        'Evet. Proje türü, metraj ve hedef konfora göre teknik yönlendirme, katman önerisi ve uygulama planlaması yapılır.',
    },
    {
      question: 'Sakarya bondex sünger fiyatı ne kadar?',
      answer:
        "Sakarya bondex sünger fiyatı metrekare (m²) başına 130 TL'den başlar. Net fiyat kalınlık, metraj ve proje kurgusuna göre değişir.",
    },
  ],
  reviews: [
    {
      authorName: 'Furkan Yılmaz',
      datePublished: '2026-04-03',
      ratingValue: 5,
      title: 'Adapazarı konut projesinde iyi sonuç',
      body:
        "Daire zemininde uyguladık. Komşu kaynaklı darbe sesi belirgin şekilde azaldı.",
      verified: true,
    },
    {
      authorName: 'Nazlı Demir',
      datePublished: '2026-04-09',
      ratingValue: 5,
      title: 'Serdivan ofisinde fark yarattı',
      body:
        "Toplantı odasında ses taşmasını azaltmak için kullandık. Beklentimizi karşıladı.",
      verified: true,
    },
    {
      authorName: 'Eren Kaya',
      datePublished: '2026-04-15',
      ratingValue: 4,
      title: 'Arifiye teknik odada memnunuz',
      body:
        'Makine kaynaklı titreşim etkisini düşürmek için uyguladık. Gürültü seviyesi daha kontrollü hale geldi.',
      verified: true,
    },
  ],
};

export function CityBondexSakarya() {
  const url = toAbsoluteUrl('/sakarya-bondex-sunger');
  const title = 'Sakarya Bondex Sünger | Yalıtım Süngeri– Echopan';
  const description =
    'Sakarya bondex sünger, konut ve teknik projelerde darbe ve titreşim kaynaklı sesleri azaltmaya yardımcı olur. Echopan kalitesiyle sunulur.';

  const relatedSlugs = ['bondex-sunger', 'bondex-sunger-20mm', 'bondex-sunger-40mm', 'bariyerli-bondex-sunger'];
  const relatedBondex = relatedSlugs.map((slug) => products.find((p) => p.slug === slug)).filter((p): p is Product => Boolean(p));

  const waUrl = buildWhatsAppUrl('Merhaba, Sakarya bondex sünger için fiyat ve teslimat bilgisi rica ediyorum. (sakarya-bondex-sunger)');
  const displayPrice = sakaryaProduct.priceTRY
    ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(sakaryaProduct.priceTRY)
    : null;

  const pageReviews =
    sakaryaProduct.reviews?.map((r) => ({
      name: r.authorName,
      date: r.datePublished,
      title: r.title,
      verified: !!r.verified,
      rating: r.ratingValue,
      text: r.body,
    })) || [];
  const avgRating =
    pageReviews.length > 0 ? pageReviews.reduce((sum, r) => sum + (Number.isFinite(r.rating) ? r.rating : 0), 0) / pageReviews.length : 0;

  const topSpecs = [
    { k: 'Stok', v: 'Sakarya içi düzenli stok' },
    { k: 'Teslim', v: '1-3 iş günü' },
    { k: 'Kalınlık', v: '10mm-50mm' },
    { k: 'Uygulama', v: 'Yapıştırma / Mekanik tespit' },
    { k: 'Hizmet', v: 'Sakarya merkez + ilçeler' },
    { k: 'Plan', v: 'Proje bazlı teknik kurgulama' },
  ];

  const breadcrumb = [
    { name: 'Anasayfa', item: toAbsoluteUrl('/') },
    { name: 'Ürünler', item: toAbsoluteUrl('/urunler') },
    { name: 'Sakarya Bondex Sünger', item: url },
  ];

  const validFromStr = new Date().toISOString().split('T')[0];
  const priceValidUntil = new Date();
  priceValidUntil.setFullYear(priceValidUntil.getFullYear() + 1);
  const priceValidUntilStr = priceValidUntil.toISOString().split('T')[0];

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${toAbsoluteUrl('/')}#website`,
        name: 'Bondex Sünger',
        url: toAbsoluteUrl('/'),
        inLanguage: 'tr-TR',
        potentialAction: {
          '@type': 'SearchAction',
          target: { '@type': 'EntryPoint', urlTemplate: `${toAbsoluteUrl('/')}urunler?q={search_term_string}` },
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'Organization',
        '@id': `${toAbsoluteUrl('/')}#organization`,
        name: 'bariyerlibondexsunger.com',
        url: toAbsoluteUrl('/'),
        logo: { '@type': 'ImageObject', url: toAbsoluteUrl('/images/bondex-logo.webp'), width: 512, height: 512 },
        contactPoint: [
          { '@type': 'ContactPoint', telephone: '+90 216 519 34 11', contactType: 'customer service', areaServed: 'TR', availableLanguage: ['tr'] },
          { '@type': 'ContactPoint', telephone: '+90 546 919 11 34', contactType: 'sales', areaServed: 'TR', availableLanguage: ['tr'] },
        ],
        sameAs: [
          'https://www.facebook.com/profile.php?id=61575206163572',
          'https://x.com/KabinPods',
          'https://www.instagram.com/kabinpods/',
          'https://www.youtube.com/channel/UCKk3RjOabrJb8JQYK9BjWkw',
          'https://www.linkedin.com/company/tasar%C4%B1m-akustik-mimarl%C4%B1k',
        ],
      },
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        url,
        name: title,
        description,
        isPartOf: { '@id': `${toAbsoluteUrl('/')}#website` },
        breadcrumb: { '@id': `${url}#breadcrumb` },
        inLanguage: 'tr-TR',
        datePublished: '2026-04-27',
        dateModified: new Date().toISOString().split('T')[0],
        primaryImageOfPage: { '@type': 'ImageObject', url: toAbsoluteUrl(sakaryaProduct.imagePath), width: 1200, height: 750 },
        about: [
          { '@type': 'Thing', name: 'Sakarya Bondex Sünger' },
          { '@type': 'Thing', name: 'Sakarya ses yalıtımı' },
          { '@type': 'Thing', name: 'teknik hacim izolasyonu' },
        ],
        speakable: { '@type': 'SpeakableSpecification', cssSelector: ['.ProductSEO', '.ProductPage h1'] },
        mainEntity: { '@id': `${url}#product` },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${url}#breadcrumb`,
        itemListElement: breadcrumb.map((b, idx) => ({ '@type': 'ListItem', position: idx + 1, name: b.name, item: b.item })),
      },
      {
        '@type': 'Product',
        '@id': `${url}#product`,
        name: sakaryaProduct.name,
        image: [{ '@type': 'ImageObject', url: toAbsoluteUrl(sakaryaProduct.imagePath), width: 1200, height: 750 }],
        description: sakaryaProduct.description,
        brand: { '@type': 'Brand', name: 'Bondex' },
        category: sakaryaProduct.category,
        url,
        sku: sakaryaProduct.sku,
        mpn: sakaryaProduct.mpn,
        productID: sakaryaProduct.sku || sakaryaProduct.mpn,
        material: 'Rebonded Süngerpan',
        audienceType: 'Business',
        additionalProperty: [
          { '@type': 'PropertyValue', name: 'Kategori', value: sakaryaProduct.category },
          { '@type': 'PropertyValue', name: 'Tip', value: 'Rebonded Süngerpan' },
          { '@type': 'PropertyValue', name: 'Hizmet Bölgesi', value: 'Sakarya' },
        ],
        offers: {
          '@type': 'Offer',
          url,
          priceCurrency: 'TRY',
          price: String(sakaryaProduct.priceTRY),
          validFrom: validFromStr,
          priceValidUntil: priceValidUntilStr,
          availability: 'https://schema.org/InStock',
          itemCondition: 'https://schema.org/NewCondition',
          seller: { '@id': `${toAbsoluteUrl('/')}#organization` },
          unitText: 'm²',
          priceSpecification: { '@type': 'UnitPriceSpecification', price: String(sakaryaProduct.priceTRY), priceCurrency: 'TRY', unitCode: 'MTK', unitText: 'm²' },
          hasMerchantReturnPolicy: { '@id': `${url}#merchantReturnPolicy` },
          shippingDetails: { '@id': `${url}#shippingDetails` },
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: avgRating.toFixed(1),
          reviewCount: pageReviews.length,
          bestRating: '5',
          worstRating: '1',
        },
        review: sakaryaProduct.reviews?.map((r) => ({
          '@type': 'Review',
          author: { '@type': 'Person', name: r.authorName },
          datePublished: r.datePublished,
          reviewBody: r.body,
          headline: r.title,
          ...(r.verified && { reviewAspect: 'Verification' }),
          reviewRating: { '@type': 'Rating', ratingValue: String(r.ratingValue), bestRating: '5', worstRating: '1' },
        })),
      },
      {
        '@type': 'MerchantReturnPolicy',
        '@id': `${url}#merchantReturnPolicy`,
        applicableCountry: { '@type': 'Country', name: 'TR' },
        returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
        merchantReturnDays: 14,
        returnMethod: 'https://schema.org/ReturnByMail',
        returnFees: 'https://schema.org/FreeReturn',
      },
      {
        '@type': 'OfferShippingDetails',
        '@id': `${url}#shippingDetails`,
        shippingRate: { '@type': 'MonetaryAmount', value: '0', currency: 'TRY' },
        shippingDestination: { '@type': 'DefinedRegion', addressCountry: { '@type': 'Country', name: 'TR' } },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          handlingTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 3, unitCode: 'DAY' },
          transitTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 5, unitCode: 'DAY' },
          businessDays: { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'] },
          cutoffTime: '14:00',
        },
      },
      {
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        mainEntity: sakaryaProduct.faqs?.map((f) => ({ '@type': 'Question', name: f.question, acceptedAnswer: { '@type': 'Answer', text: f.answer } })),
      },
    ],
  };

  const tabItems = [
    {
      key: 'aciklama',
      label: 'Açıklama',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Sakarya'da Bondex Sünger Kullanımı Neden Farklı Planlanmalı?</h2>
            <p className={styles.seoText}>
              Sakarya'da ses yalıtımı ihtiyacını tek cümleyle özetlemek zordur; çünkü şehirde hem konut ağırlıklı yaşam alanları hem de
              üretim/lojistik odaklı teknik alanlar bir arada bulunur. Adapazarı, Serdivan, Erenler gibi merkez yoğunluğu olan bölgelerde
              kullanıcı şikayetleri çoğunlukla katlar arası darbe sesi ve komşu duvar geçişine odaklanır. Arifiye, Hendek, Akyazı, Karasu
              gibi bölgelerde ise proje tipine göre teknik oda, atölye, makine hacmi veya karma kullanım yapıları öne çıkabilir.
            </p>
            <p className={styles.seoText}>
              Bu nedenle Sakarya bondex sünger çözümü, klasik "ürün al ve uygula" yaklaşımından çok daha fazlasını gerektirir. Rebonded
              süngerpan yapısı darbe ve titreşim kaynaklı enerjiyi düşürmeye yardımcı olsa da gerçek performans; doğru kalınlık seçimi, doğru
              yüzey hazırlığı, birleşimlerin doğru kapanması ve proje sıralamasının doğru kurgulanmasıyla elde edilir. Sorunu doğru okumadan
              seçilen kalınlık, çoğu projede hem maliyet hem memnuniyet tarafında zayıf kalır.
            </p>
            <p className={styles.seoText}>
              Sakarya'nın dönüşen yapı stoğu nedeniyle renovasyon projeleri de artmaktadır. Eski yapılarda düzensiz yüzey, farklı dönemlere
              ait tadilat izleri ve tesisat geçişleri sık görüldüğü için uygulama öncesi kontrol daha kritik hale gelir. Bu kontrol süreci
              doğru yapılırsa, aynı metrajla daha görünür sonuç alınır.
            </p>
            <h3 className={styles.seoSubtitle}>Konut Projelerinde Beklenti ve Çözüm Dengesi</h3>
            <p className={styles.seoText}>
              Konut tarafında en sık hedef gece rahatsızlığını azaltmaktır. Üst kat adım sesi, sürükleme ve noktasal darbe sesleri yaşam
              kalitesini düşürdüğünde kullanıcı "tam sessizlik" bekleyebilir; ancak teknik açıdan doğru hedef, rahatsızlığı belirgin azaltan
              sürdürülebilir konfordur. Bu yaklaşım hem maliyetin yönetilebilir kalmasını hem de uygulama sonrası beklentinin gerçekçi
              olmasını sağlar.
            </p>
            <p className={styles.seoText}>
              Sakarya'da daire yenilemelerinde oda bazlı etaplama modeli verimli sonuç verir. Çocuk odası, yatak odası ve çalışma alanı gibi
              hassas hacimlere öncelik verildiğinde, toplam metraj sınırlı olsa bile kullanıcı tarafında hissedilen iyileşme artar. Bu model
              aynı zamanda yaşamın tamamen durmadan devam etmesine olanak tanır.
            </p>
            <h3 className={styles.seoSubtitle}>Ofis ve Ticari Yapılarda Akustik Disiplin</h3>
            <p className={styles.seoText}>
              Serdivan ve merkez iş aksında ofis projelerinde en yaygın şikayetlerden biri toplantı odası konuşmalarının dışarı taşmasıdır.
              Bu sorun yalnızca duvar katmanı ile çözülemez; kapı çevresi, tavan birleşimi, elektrik/tesisat geçişleri ve köşe noktaları da
              birlikte ele alınmalıdır. Bondex sünger bu alanlarda doğru detayla kullanıldığında konuşma mahremiyeti ve çalışma konforu açısından
              gözle görülür katkı sağlar.
            </p>
            <p className={styles.seoText}>
              Açık ofis projelerinde ise amaç mutlak sessizlik değil, sürekli uğultu baskısını düşürmektir. Bölgesel uygulama yaklaşımıyla
              çağrı alanı, toplantı odası ve ortak çalışma hacimleri farklı kurgulanırsa daha dengeli sonuç elde edilir.
            </p>
            <h3 className={styles.seoSubtitle}>Teknik Oda ve Makine Hacimlerinde Neye Dikkat Edilmeli?</h3>
            <p className={styles.seoText}>
              Sakarya'nın sanayi etkisi taşıyan bölgelerinde teknik hacim uygulamaları önemli yer tutar. Kompresör, fan, pompa ve benzeri
              ekipmanların bulunduğu odalarda ses çoğu zaman yalnızca hava kaynaklı değil, aynı zamanda titreşim kaynaklıdır. Bu nedenle
              çözümde kaynak noktaya yakın uygulama, taşıyıcı teması azaltma ve birleşim sürekliliği birlikte planlanmalıdır.
            </p>
            <p className={styles.seoText}>
              Teknik projelerde bir diğer kritik konu bakım erişimidir. Uygulama sonrası servis alanları kapatılırsa işletme sorunları oluşur.
              Bu yüzden çözüm modeli, teknik performansla birlikte bakım operasyonunu da koruyacak şekilde tasarlanmalıdır.
            </p>
            <h3 className={styles.seoSubtitle}>Kalınlık Kararı Nasıl Verilir?</h3>
            <p className={styles.seoText}>
              Kalınlık seçimi yapılırken önce şikayetin tipi belirlenmelidir. Hafif-orta senaryolarda 20-30mm dengeli bir çözüm olurken, yüksek
              şikayetli alanlarda 40mm ve üzeri seçenekler daha uygun hale gelebilir. Bazı projelerde bariyerli sistemlere geçmek daha doğru
              sonuç verir. Bu nedenle model kıyaslaması için{' '}
              <Link to="/bondex-sunger" rel="follow">bondex sünger</Link> ve{' '}
              <Link to="/bariyerli-bondex-sunger" rel="follow">bariyerli bondex</Link> seçenekleri birlikte değerlendirilmelidir.
            </p>
            <p className={styles.seoText}>
              Gereksiz kalınlık kullanımı maliyeti artırır; yetersiz kalınlık ise sonuç beklentisini düşürür. En iyi yaklaşım, kritik bölgeleri
              güçlendiren ve düşük riskli bölgelerde optimize kalan dengeli bir katman planıdır.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'teknik',
      label: 'Teknik Özellikler',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Sakarya Teknik Özellikler Özeti</h2>
            <h3 className={styles.seoSubtitle}>Malzeme Yapısı</h3>
            <p className={styles.seoText}>
              Rebonded süngerpan yapı, darbe ve titreşim kaynaklı enerjinin yüzey boyunca yayılımını azaltmaya destek olur.
            </p>
            <h3 className={styles.seoSubtitle}>Uygulama Disiplini</h3>
            <p className={styles.seoText}>
              Performansın sürdürülebilir olması için yüzey hazırlığı, birleşim kontrolü ve kenar sürekliliği adımları atlanmamalıdır.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'kullanim',
      label: 'Kullanım Alanları',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Sakarya'da Kullanım Alanları</h2>
            <h3 className={styles.seoSubtitle}>Konut ve Rezidans</h3>
            <p className={styles.seoText}>
              Katlar arası darbe sesi ve komşu duvar kaynaklı konuşma geçişinin azaltılmasında kullanılır.
            </p>
            <h3 className={styles.seoSubtitle}>Ofis ve Ticari Yapı</h3>
            <p className={styles.seoText}>
              Toplantı odası mahremiyeti ve çalışma alanı konforunu artırmaya yönelik çözümlerde tercih edilir.
            </p>
            <h3 className={styles.seoSubtitle}>Teknik Hacim</h3>
            <p className={styles.seoText}>
              Ekipman kaynaklı titreşim ve gürültü etkisini düşürmek için teknik oda uygulamalarında kullanılır.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'opsiyonel',
      label: 'Opsiyonel',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Teslimat ve Süreç Yönetimi</h2>
            <h3 className={styles.seoSubtitle}>Etaplı Sevkiyat</h3>
            <p className={styles.seoText}>
              Büyük metrajlı işlerde etaplı sevkiyat sahadaki düzeni korur ve uygulama hızını artırır.
            </p>
            <h3 className={styles.seoSubtitle}>Teknik Yönlendirme</h3>
            <p className={styles.seoText}>
              Keşif, metraj doğrulama, katman önerisi ve uygulama sonrası kontrol adımları birlikte yönetildiğinde sonuç daha stabil olur.
            </p>
          </section>
        </div>
      ),
    },
  ];

  return (
    <main className="container ProductPage">
      <JsonLd data={schema} />

      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/">Anasayfa</Link>
        <span aria-hidden="true">/</span>
        <Link to="/urunler">Ürünler</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">Sakarya Bondex Sünger</span>
      </nav>

      <div className={styles.heroGrid}>
        <div className={styles.media}>
          <img className={styles.img} src={sakaryaProduct.imagePath} alt="Sakarya bondex sünger uygulaması" loading="eager" width="600" height="400" />
        </div>
        <section className={`ProductSEO card ${styles.panel}`}>
          <h1 className={styles.h1}>Sakarya Bondex Sünger</h1>
          <p className={styles.modelLine}>
            <span className={styles.modelLabel}>Model:</span> BDX-SKY | Sakarya İçi Projeler
          </p>
          <p className={styles.meta}>
            Sakarya'da <strong>bondex sünger</strong> ihtiyacı olan konut, ofis ve teknik hacim projelerinde doğru kalınlık, hızlı tedarik
            ve proje bazlı uygulama yaklaşımı ile ilerliyoruz.
          </p>

          {pageReviews.length > 0 && (
            <div className={styles.ratingRow}>
              <div className={styles.stars}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <IconStar key={star} style={{ opacity: star <= Math.round(avgRating) ? 1 : 0.3 }} />
                ))}
              </div>
              <span className={styles.ratingText}>
                {avgRating.toFixed(1)} ({pageReviews.length} değerlendirme)
              </span>
            </div>
          )}

          {displayPrice && (
            <div className={styles.priceRow}>
              <span className={styles.price}>{displayPrice}</span>
              <span className={styles.priceUnit}>/ m²'den başlayan</span>
            </div>
          )}

          <div className={styles.badges}>
            <span className="chip">Bondex Sünger</span>
            <span className="chip">Sakarya içi hızlı teslimat</span>
            <span className="chip">Konut ve teknik hacme uygun</span>
          </div>

          <div className={styles.specGrid} aria-label="Sakarya için özet bilgiler">
            {topSpecs.map((spec) => (
              <div key={spec.k} className={styles.specItem}>
                <div className={styles.specK}>{spec.k}</div>
                <div className={styles.specV}>{spec.v}</div>
              </div>
            ))}
          </div>

          <div className={styles.ctaRow}>
            <a href={waUrl} target="_blank" rel="noopener noreferrer" className={`btn btn-primary ${styles.ctaBtn}`}>
              <IconWhatsApp />
              <span>WhatsApp ile Teklif Al</span>
            </a>
            <a href={`tel:${SALES_PHONE_READABLE.replace(/\s/g, '')}`} className={`btn btn-outline ${styles.ctaBtn}`}>
              <IconPhone />
              <span>{SALES_PHONE_READABLE}</span>
            </a>
          </div>
        </section>
      </div>

      <div className={styles.contentGrid}>
        <section>
          <div className={`card ${styles.sectionCard}`} style={{ padding: 0 }}>
            <Tabs items={tabItems} defaultKey="aciklama" />
          </div>
        </section>
      </div>

      {pageReviews.length > 0 && (
        <section className={styles.reviewSection}>
          <div className={styles.reviewHeader}>
            <h2 className={styles.reviewTitle}>Müşteri Değerlendirmeleri</h2>
            <div className={styles.reviewUnderline} aria-hidden="true" />
          </div>
          <div className={styles.reviewGrid}>
            {pageReviews.map((review, idx) => (
              <div key={idx} className={styles.reviewCard}>
                <div className={styles.reviewTop}>
                  <div className={styles.reviewStars}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <IconStar key={star} style={{ opacity: star <= review.rating ? 1 : 0.3 }} />
                    ))}
                  </div>
                  {review.verified && <span className={styles.verifiedBadge}>✓ Doğrulanmış</span>}
                </div>
                <h3 className={styles.reviewName}>{review.title}</h3>
                <p className={styles.reviewText}>{review.text}</p>
                <div className={styles.reviewFooter}>
                  <span className={styles.reviewAuthor}>{review.name}</span>
                  <span className={styles.reviewSeparator}>•</span>
                  <span className={styles.reviewDate}>{review.date}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {sakaryaProduct.faqs?.length ? (
        <section className={styles.faqSection}>
          <div className={styles.faqHeader}>
            <h2 className={styles.faqTitle}>Sakarya Bondex Sünger – Sıkça Sorulan Sorular</h2>
            <div className={styles.faqUnderline} aria-hidden="true" />
          </div>
          <div className={styles.faqCard}>
            <Accordion items={sakaryaProduct.faqs} variant="seo" />
          </div>
        </section>
      ) : null}

      {relatedBondex.length ? (
        <section className={styles.relatedSection}>
          <div className={styles.relatedHeader}>
            <h2 className={styles.relatedTitle}>Diğer Bondex Sünger Modelleri</h2>
            <div className={styles.relatedUnderline} aria-hidden="true" />
          </div>
          <div className={styles.relatedGrid}>
            {relatedBondex.map((p) => (
              <div key={p.slug} className={styles.relatedItem}>
                <ProductCard product={p} />
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </main>
  );
}
